/* eslint-disable sonarjs/cognitive-complexity */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  BUY_EXCL,
  CHOOSE_YOUR_OPTIONS,
  PLEASE_CHOOSE_ONE_PRODUCT,
  DEAL_DAILY_CAP_REACHED,
  DEAL_LIMITS_REACHED,
  GIVE_AS_A_GIFT,
  BESTSELLER_EXPLAIN,
  NIBBLE_TIMER,
} from '../../../../config/text/text';
import { calculateAdminFee, showEstimated } from '../../../../helpers/adminFee';
import { trackEvent } from '../../../../helpers/analytics';
import { formatCurrency } from '../../../../helpers/currency';
import {
  getIsChooseYourProductDeal,
  getIsLeadGen,
  getTotalRemaining,
  isDealAlreadyInBasket,
  isExist,
} from '../../../../helpers/deals';
import ThemeContext from '../../../../providers/ThemeProvider';
import Message from '../../../_generic/Message';
import PrimaryButton from '../../../_generic/button/PrimaryButton';
import {
  isNibbleEnabled,
  nibbleDwellTimer,
} from '../../../nibble/NibbleHelper';
import NibbleWidget from '../../../nibble/NibbleWidget';
import DealBuyButton from './DealBuyButton';
import DealChooseYourOptionsClaimMessage from './DealChooseYourOptionsClaimMessage';
import DealChooseYourOptionsItem from './DealChooseYourOptionsItem';
import DealGiftButton from './DealGiftButton';

const DealChooseYourOptionsMulti = ({
  deal = {},
  selectedOptions = [],
  showError = false,
  onChangeValues = () => {},
  onSetShowMultiError = () => {},
  className = '',
  isSoldOut = false,
  dynamicPricingReady = false,
  text = null,
  isClaim = false,
  isExperienceBox = false,
  isProductPage = false,
}) => {
  const theme = useContext(ThemeContext);
  const isLeadGen = getIsLeadGen(deal);
  const selectedOptionsSum = selectedOptions.reduce((a, b) => a + b, 0);
  const hasSelection = selectedOptionsSum > 0;
  const userBasket = useSelector((state) => state.basket.basket);
  const [isVipUser, basketTotal] = useSelector((state) => [
    state?.user?.userprofile?.vipUser,
    state?.user?.basketTotal,
  ]);
  const dealsInBasket = isDealAlreadyInBasket(deal.id, userBasket.products);
  const bestsellerProduct = deal?.products?.find(
    (element) => element.bestSellerClaim,
  );
  const [showNibble, setShowNibble] = useState(false);
  const hasTracked = useRef(false);
  const didTrackNibblePromtClick = useRef(false);
  const didTrackNibble = useRef(false);

  useEffect(() => {
    const validSelections = selectedOptions.filter((q) => q > 0).length;
    if (validSelections !== 1) return setShowNibble(false);

    const selectedIndex = selectedOptions.findIndex((q) => q > 0);
    const selectedProduct = deal?.products?.[selectedIndex];
    if (!selectedProduct) return;

    const { id: productId } = selectedProduct;
    const nibbleCheck = (flag) =>
      isNibbleEnabled(deal, productId, isVipUser, flag);
    const localStorageFlag = localStorage.getItem(NIBBLE_TIMER);

    if (localStorageFlag && !didTrackNibblePromtClick.current) {
      const [appear, wouldAppear] = [false, true].map(nibbleCheck);
      appear && trackEvent('nibble_button_appear_from_prompt_click');
      wouldAppear && trackEvent('nibble_button_would_appear_from_prompt_click');
      didTrackNibblePromtClick.current = true;
      if (wouldAppear) didTrackNibble.current = true;
      if (appear) hasTracked.current = true;
    }

    nibbleDwellTimer().then(() => {
      const [wouldLoad, shouldAppear] = [true, false].map(nibbleCheck);

      wouldLoad &&
        !didTrackNibble.current &&
        trackEvent('nibble_button_would_have_loaded');
      didTrackNibble.current ||= wouldLoad;

      if (shouldAppear && !hasTracked.current) {
        trackEvent('nibble_button_appear');
        hasTracked.current = true;
        localStorage.setItem(NIBBLE_TIMER, 'true');
      }
      setShowNibble(true);
    });
  }, [selectedOptions, deal, isVipUser]);

  useEffect(() => {
    return () => {
      hasTracked.current = false;
    };
  }, []);

  let totalRemainingForDay = getTotalRemaining(deal);
  if (dealsInBasket && isExist(totalRemainingForDay))
    totalRemainingForDay -= dealsInBasket;

  const [remainingQuantity, setRemainingQuantity] = useState(
    totalRemainingForDay,
  );

  useEffect(() => {
    setRemainingQuantity(totalRemainingForDay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealsInBasket]);

  useEffect(() => {
    if (remainingQuantity !== null && remainingQuantity >= 0) {
      setRemainingQuantity(totalRemainingForDay - selectedOptionsSum);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions]);

  if (!deal || !deal.products) return null;
  if (!getIsChooseYourProductDeal(deal) || isProductPage) return null;

  const onQuantityChange = (event, index) => {
    onSetShowMultiError(false);
    onChangeValues(
      selectedOptions.map((quantity, selectOptionsIndex) =>
        selectOptionsIndex === index
          ? Number.parseInt(event.currentTarget.value, 10)
          : quantity,
      ),
    );
  };

  const getGiftButton = (itemSelected = false) => {
    if (!deal.giftable || isClaim || isExperienceBox) return null;
    if (!itemSelected)
      return (
        <div className="gift-button">
          <PrimaryButton
            aria-label={GIVE_AS_A_GIFT}
            className="full-width outline margin"
            disabled={isSoldOut}
            onClick={() => onSetShowMultiError(true)}
          >
            {GIVE_AS_A_GIFT}
          </PrimaryButton>
        </div>
      );

    return (
      <div className="dealgift-button">
        <DealGiftButton
          buttonType="inside_product_options"
          className="full-width outline margin"
          deal={deal}
          isSoldOut={isSoldOut}
          position={selectedOptions}
        />
      </div>
    );
  };

  const dealCapText =
    remainingQuantity <= 0 && selectedOptionsSum === 0 && dealsInBasket
      ? DEAL_LIMITS_REACHED
      : DEAL_DAILY_CAP_REACHED.replace('###dealcap###', totalRemainingForDay);

  const estAdminFee = () => {
    if (deal?.products[0]?.adminFee > 0) {
      const calAdminFee = calculateAdminFee(
        (basketTotal?.adminFeeApplicableOrderAmount || 0) + deal.price,
        true,
      );
      const totalAdminFee = basketTotal?.totalAdminFee || 0;
      if (calAdminFee > totalAdminFee) {
        return calAdminFee - totalAdminFee;
      }
    }

    return null;
  };

  return (
    <div
      className={classNames(
        'choose-your-options-multi-select cyo-wrapper',
        className,
      )}
    >
      <div className="cyo-block">
        <div className="cyo-header">{CHOOSE_YOUR_OPTIONS}</div>
        <div className="cyo-body">
          <div className="cyo-options">
            {deal.products.map((item, index) => (
              <DealChooseYourOptionsItem
                deal={deal}
                index={index}
                isClaim={isClaim}
                item={item}
                key={`cyo-options-${deal.id}-${index}`}
                onQuantityChange={(event) => onQuantityChange(event, index)}
                quantity={selectedOptions[index] || 0}
                remainingQuantity={remainingQuantity}
              />
            ))}
          </div>
          {showError && (
            <div className={`cyo-error-wrap`}>
              <div className="cyo-error-message">
                {PLEASE_CHOOSE_ONE_PRODUCT}
              </div>
            </div>
          )}
          {remainingQuantity !== null && remainingQuantity <= 0 && (
            <div className={`cyo-error-wrap`}>
              <Message message={dealCapText} showIcon={false} type="INFO" />
            </div>
          )}
          {isClaim && (
            <div className={`cyo-error-wrap`}>
              <DealChooseYourOptionsClaimMessage
                deal={deal}
                selectedOptions={selectedOptions}
              />
            </div>
          )}
          <div className="cyo-buttons">
            {!hasSelection && (
              <>
                <div className="buy-button">
                  <PrimaryButton
                    aria-label={text ? text : BUY_EXCL}
                    className="buy-button size-larger deal-main-deal__buy full-width margin"
                    disabled={isSoldOut}
                    onClick={() => onSetShowMultiError(true)}
                  >
                    {text ? text : BUY_EXCL}
                  </PrimaryButton>
                </div>
                {getGiftButton(false)}
              </>
            )}
            {hasSelection && (
              <>
                <div className="dealbuy-button">
                  <DealBuyButton
                    buttonType="inside_product_options"
                    className={
                      'size-larger deal-main-deal__buy full-width margin'
                    }
                    deal={deal}
                    isClaim={isClaim}
                    isSoldOut={isSoldOut}
                    position={selectedOptions}
                    text={text}
                  />
                </div>
                {
                  /* Only show NibbleWidget if exactly one product is selected **and** timer has finished */
                  selectedOptions.filter((quantity) => quantity > 0).length ===
                    1 &&
                    showNibble && (
                      <NibbleWidget
                        deal={deal}
                        quantity={selectedOptions.find(
                          (quantity) => quantity > 0,
                        )}
                        variantId={
                          deal.products[
                            selectedOptions.findIndex(
                              (quantity) => quantity > 0,
                            )
                          ]?.id
                        }
                      />
                    )
                }
                {getGiftButton(true)}
              </>
            )}
          </div>
        </div>
      </div>
      {estAdminFee() && (
        <p className="admin-fee-condition">
          *Estimated admin fee of{' '}
          {formatCurrency(deal.currency, estAdminFee(), true, false)} based on
          checkout basket value applies.
        </p>
      )}
      {bestsellerProduct && (
        <p className="admin-fee-condition-best-selling">
          *{BESTSELLER_EXPLAIN}
        </p>
      )}
      <style jsx>{`
        .admin-fee-condition {
          margin: 0px;
          font-size: 12px;
        }

        .admin-fee-condition-best-selling {
          margin: 0px;
          margin-bottom: 20px;
          font-size: 12px;
        }

        .choose-your-options-multi-select {
          margin-bottom: 20px;
        }
        .cyo-body {
          display: flex;
          flex-direction: column;
          border: 4px solid ${theme.colors.primary};
          border-top: none;
        }
        .cyo-error-wrap.hide {
          display: none;
        }
        .cyo-error-wrap.show {
          display: block;
        }
        .cyo-error-wrap {
          padding-left: 20px;
          padding-right: 20px;
          margin-bottom: 20px;
        }
        .cyo-error-message {
          margin: 0 auto;
          padding: 6px 12px;
          color: ${theme.commoncolors.alert};
          border: 4px solid ${theme.commoncolors.alert};
          border-radius: 4px;
          text-align: center;
          font-weight: bold;
        }
        .cyo-buttons {
          padding-left: 20px;
          padding-right: 20px;
        }
        :global(.cyo-row__numbers) {
          opacity: ${dynamicPricingReady ? '1.0' : '0.01'};
        }
        .cyo-header {
          background-color: ${theme.colors.primary};
          min-height: 50px;
          display: flex;
          flex-direction: column;
          justify-content: space-around;
          text-align: center;
          font-size: 20px;
          color: ${theme.colors.textonprimary};
          text-transform: capitalize;
        }
        :global(.cyo-error-wrap .message) {
          margin-bottom: 0;
        }
        @media (max-width: ${theme.breakpoints.smDown}) {
          .cyo-main-deal .cyo-buttons {
            display: ${isLeadGen ? 'block' : 'none'};
          }
          .cyo-main-deal .cyo-options {
            order: 2;
          }
          .cyo-main-deal .cyo-error-wrap {
            order: 1;
            margin: 20px 0 0 0;
          }
        }
      `}</style>
    </div>
  );
};

DealChooseYourOptionsMulti.propTypes = {
  className: PropTypes.string,
  deal: PropTypes.object,
  dynamicPricingReady: PropTypes.bool,
  isClaim: PropTypes.bool,
  isExperienceBox: PropTypes.bool,
  isProductPage: PropTypes.bool,
  isSoldOut: PropTypes.bool,
  onChangeValues: PropTypes.func,
  onSetShowMultiError: PropTypes.func,
  selectedOptions: PropTypes.arrayOf(PropTypes.number),
  showError: PropTypes.bool,
  text: PropTypes.string,
};

export default DealChooseYourOptionsMulti;
